import { type ReactNode, useCallback } from 'react';
import { faTimes, faChevronLeft } from '@fortawesome/pro-regular-svg-icons';
import { Image } from 'cloudinary-react';
import Router from 'next/router';

import cx from '@/lib/cx';
import useScreen from '@/hooks/useScreen';
import Link from '@/components/common/Link';
import Navbar from '@/components/common/Navbar';
import NavbarIconButton from '@/components/common/NavbarIconButton';
import NavbarRoundedButton from '@/components/common/NavbarRoundedButton';
import NavbarTitle from '@/components/common/NavbarTitle';

import LogoKey from '../../svg/logo-key.svg';

type WorkflowLayoutProps = {
  title?: string;
  logo?: string;
  logoFallbackText?: string;
  backAction?: 'back' | 'close';
  onBack?: () => void;
  rightAction?: ReactNode;
  showRecaptchaDisclaimer?: boolean;
  children?: ReactNode;
  className?: string;
};

const WorkflowLayout = ({
  title,
  logo,
  logoFallbackText,
  backAction,
  onBack = Router.back,
  rightAction = null,
  showRecaptchaDisclaimer = false,
  children = null,
  className,
}: WorkflowLayoutProps) => {
  const screen = useScreen();

  const renderNavbarButton = useCallback(
    ({ label, icon, ...props }) => {
      if (screen.lg) {
        return (
          <NavbarRoundedButton label={label} icon={icon} data-testid="back-button" {...props} />
        );
      }

      return <NavbarIconButton {...props} icon={icon} data-testid="back-button" border />;
    },
    [screen.lg]
  );

  return (
    <div className={cx('bg-white sm:bg-gray-100 min-h-screen flex flex-col', className)}>
      <Navbar
        className="sticky top-0 z-40 h-20 bg-white border-b border-gray-300 shrink-0"
        left={
          backAction &&
          renderNavbarButton({
            as: 'button',
            type: 'button',
            onClick: onBack,
            ...{
              back: { label: 'Back', icon: faChevronLeft },
              close: { label: 'Close', icon: faTimes },
            }[backAction],
          })
        }
        center={(() => {
          if (title) return <NavbarTitle title={title} />;
          if (!logo) return <NavbarTitle title={logoFallbackText} />;

          return (
            <Image
              publicId={logo}
              alt=""
              width={260}
              height={120}
              crop="limit"
              quality="auto"
              fetchFormat="auto"
              className="block h-10"
            />
          );
        })()}
        right={rightAction}
      />

      <main className="grow pt-8 sm:pt-16 flex flex-col items-stretch">{children}</main>

      {(screen.sm || showRecaptchaDisclaimer) && (
        <footer className="shrink-0 container text-center pt-8 pb-24 grid grid-cols-1 gap-y-4">
          <div className="lowercase text-lg flex justify-center items-center text-gray-500 gap-3">
            Powered by <LogoKey className="fill-current inline-block h-[1.625rem]" />
          </div>
          <div>
            <p className="text-sm text-gray-500 max-w-sm mx-auto">
              Copyright &copy;{new Date().getFullYear()} • All rights reserved{' '}
            </p>
            <p className="text-sm text-gray-500 max-w-sm mx-auto">
              <Link
                href="/privacy"
                external
                className="underline hover:no-underline hover:text-gray-600"
              >
                Privacy Policy
              </Link>{' '}
              •{' '}
              <Link
                href="/tos"
                external
                className="underline hover:no-underline hover:text-gray-600"
              >
                Terms of Service
              </Link>
            </p>
          </div>

          {showRecaptchaDisclaimer && (
            <p className="text-sm text-gray-500 max-w-sm mx-auto">
              This site is protected by reCAPTCHA and the Google{' '}
              <Link
                href="https://policies.google.com/privacy"
                className="underline hover:no-underline hover:text-gray-600"
              >
                Privacy Policy
              </Link>{' '}
              and{' '}
              <Link
                href="https://policies.google.com/terms"
                className="underline hover:no-underline hover:text-gray-600"
              >
                Terms of Service
              </Link>{' '}
              apply.
            </p>
          )}
        </footer>
      )}
    </div>
  );
};

export default WorkflowLayout;
